<template>
  <!--
    证书位图的 HTML 复刻组件（内嵌 SVG 排版）
    - E 档版式（2026-09-08 比例+合规修正）：
      1) 比例改为竖版 4:5（viewBox 400x500），贴近真实证书竖版方向（高企证书 300x420mm 竖版）
      2) 每张右下角加「示意图」角标（合规：明确非扫描件）
      3) 中部落持证人「海南华德科技有限公司」（真实持证主体，非杜撰）
    - 原 PNG 位图保留在 src/assets/images/20250724/rongyu/ 下，未删除
    - 2026-09-17 末（终稿·仅首页）：与证书页 NewsInformation.vue 内 9 张对齐——
      金外框(#b8923f) + 金内线(#c9a95e) + 暖米黄纸面(linear-gradient) + 顶部卷草花饰 SVG，
      彻底弃用 cert-bg-brand.png 与矢量玫红框/六色带。
      标题 #bf1522 / 主体·落款 #161616 / 花饰 #1a1a1a（与证书页完全一致）。
  -->
  <div
    v-if="cfg"
    class="cert-replica"
    role="img"
    :aria-label="cfg.label + '（示意图）'"
  >
    <div class="cert-frame">
      <div class="cert-paper">
        <svg
          class="cert-art"
          viewBox="0 0 400 500"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <!-- 顶部卷草花饰分隔线（与 NewsInformation.vue 完全一致） -->
          <g transform="translate(64 64) scale(0.85)">
            <line x1="10" y1="12" x2="116" y2="12" stroke="#1a1a1a" stroke-width="2"/>
            <line x1="204" y1="12" x2="310" y2="12" stroke="#1a1a1a" stroke-width="2"/>
            <rect x="2" y="8" width="8" height="8" transform="rotate(45 6 12)" fill="#1a1a1a"/>
            <rect x="310" y="8" width="8" height="8" transform="rotate(45 314 12)" fill="#1a1a1a"/>
            <path d="M122 12 C130 3 142 4 148 11" fill="none" stroke="#1a1a1a" stroke-width="1.8"/>
            <path d="M198 12 C190 3 178 4 172 11" fill="none" stroke="#1a1a1a" stroke-width="1.8"/>
            <path d="M148 11 C152 6.5 157 6.5 160 11 M160 11 C163 6.5 168 6.5 172 11" fill="none" stroke="#1a1a1a" stroke-width="1.5"/>
            <rect x="155.5" y="7.5" width="9" height="9" transform="rotate(45 160 12)" fill="#1a1a1a"/>
          </g>
          <!-- 证书标题（可多行） -->
          <text
            v-for="(t, i) in cfg.titles"
            :key="'t' + i"
            class="cert-art-title"
            x="200"
            :y="t.y"
            text-anchor="middle"
            :font-size="t.size || 40"
            letter-spacing="2.5"
            fill="#bf1522"
          >{{ t.text }}</text>
          <!-- 持证人（证书主体：本公司） -->
          <text
            v-if="cfg.recipient"
            class="cert-art-recipient"
            x="200"
            :y="cfg.recipient.y"
            text-anchor="middle"
            font-size="26"
            letter-spacing="2"
            fill="#161616"
          >{{ cfg.recipient.text }}</text>
          <!-- 中部小花饰（单落款时补视觉重心） -->
          <g v-if="cfg.dividerY" :transform="'translate(112 ' + cfg.dividerY + ') scale(0.55)'">
            <line x1="10" y1="12" x2="116" y2="12" stroke="#1a1a1a" stroke-width="2"/>
            <line x1="204" y1="12" x2="310" y2="12" stroke="#1a1a1a" stroke-width="2"/>
            <rect x="2" y="8" width="8" height="8" transform="rotate(45 6 12)" fill="#1a1a1a"/>
            <rect x="310" y="8" width="8" height="8" transform="rotate(45 314 12)" fill="#1a1a1a"/>
            <path d="M122 12 C130 3 142 4 148 11" fill="none" stroke="#1a1a1a" stroke-width="1.8"/>
            <path d="M198 12 C190 3 178 4 172 11" fill="none" stroke="#1a1a1a" stroke-width="1.8"/>
            <path d="M148 11 C152 6.5 157 6.5 160 11 M160 11 C163 6.5 168 6.5 172 11" fill="none" stroke="#1a1a1a" stroke-width="1.5"/>
            <rect x="155.5" y="7.5" width="9" height="9" transform="rotate(45 160 12)" fill="#1a1a1a"/>
          </g>
          <!-- 落款（可多行，锚底） -->
          <text
            v-for="(s, i) in cfg.issuers"
            :key="'s' + i"
            class="cert-art-issuer"
            x="200"
            :y="s.y"
            text-anchor="middle"
            font-size="23"
            letter-spacing="1.9"
            fill="#161616"
          >{{ s.text }}</text>
        </svg>
      </div>
    </div>
    <!-- 合规角标：明确为版式示意，非扫描件 -->
    <span class="cert-schema-tag">示意图</span>
  </div>
</template>

<script>
// 证书版式配置：与 NewsInformation.vue 内 9 张复刻同一规范（金框 + 暖黄纸 + 卷草花饰）
const RECIPIENT = "海南华德科技有限公司";
const CERTS = {
  gaoxin: {
    label: "高新技术企业证书",
    titles: [{ text: "高新技术企业证书", y: 150, size: 38 }],
    recipient: { text: RECIPIENT, y: 228 },
    issuers: [
      { text: "海南省科学技术厅", y: 352 },
      { text: "海南省财政厅", y: 398 },
      { text: "国家税务总局海南省税务局", y: 444 },
    ],
  },
  zhongzi: {
    label: "海南省高新技术种子企业",
    titles: [
      { text: "海南省高新技术", y: 128 },
      { text: "种子企业", y: 184 },
    ],
    recipient: { text: RECIPIENT, y: 250 },
    dividerY: 345,
    issuers: [{ text: "海南省科学技术厅", y: 430 }],
  },
  chuangxin: {
    label: "创新型中小企业",
    titles: [{ text: "创新型中小企业", y: 150 }],
    recipient: { text: RECIPIENT, y: 228 },
    dividerY: 340,
    issuers: [{ text: "海南省工业和信息化厅", y: 430 }],
  },
  keji: {
    label: "国家科技型中小企业",
    titles: [{ text: "国家科技型中小企业", y: 150, size: 38 }],
    recipient: { text: RECIPIENT, y: 228 },
    dividerY: 340,
    issuers: [{ text: "中华人民共和国工业和信息化部", y: 430 }],
  },
};

export default {
  name: "CertReplica",
  props: {
    cert: { type: String, required: true },
  },
  computed: {
    cfg() {
      if (CERTS[this.cert]) return CERTS[this.cert];
      // 兜底：配置缺失时不渲染空白（老问题），改用占位证书 + 控制台告警，便于快速定位
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "[CertReplica] 未找到证书配置：${this.cert}，已回退为占位版式"
        );
      }
      return {
        label: this.cert || "企业荣誉证书",
        titles: [{ text: "荣誉证书", y: 150 }],
        recipient: { text: RECIPIENT, y: 228 },
        dividerY: 340,
        issuers: [{ text: "海南华德科技有限公司", y: 430 }],
      };
    },
  },
};
</script>

<style scoped>
/* 结构：.cert-replica（暖黄纸面）> .cert-frame（金外框）> .cert-paper（金内线+纸面）> svg.cert-art */
/* 用 padding-bottom 撑 4:5 竖版比例 + 绝对定位，兼容不支持 aspect-ratio 的老内核 */
/* ⚠️ 以下样式与 NewsInformation.vue 证书页完全一致（2026-09-17 首页对齐证书页） */
.cert-replica {
  position: relative;
  width: 100%;
  /* ⚠️ 这里不能加 max-width 来限尺寸：padding-bottom:125% 是按「父容器宽度」计算的，
     一旦元素自身被 max-width 收窄，宽高比立刻失真（实测 300 宽却撑出 468 高，金框被拉高）。
     尺寸限制请加在父容器 .img-box 上（见 NewsInformation.vue / HomePage2.vue）。 */
  height: 0;
  padding-bottom: 125%;
  background: linear-gradient(160deg, #f8f0dc, #f2e6ca 55%, #f5ebd4);
  box-sizing: border-box;
  display: block;
  transition: transform 0.3s ease;
}

.img-box:hover .cert-replica {
  transform: scale(1.05);
}

.cert-frame {
  position: absolute;
  top: 2.1%;
  right: 2.1%;
  bottom: 2.1%;
  left: 2.1%;
  box-sizing: border-box;
  border: 0.32em solid #b8923f;
  padding: 0.26em;
  box-shadow: inset 0 0 0 1px rgba(184, 146, 63, 0.22);
}

.cert-paper {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border: 0.12em solid #c9a95e;
  background: radial-gradient(120% 100% at 50% 38%, #fffef9 55%, #f8f1e0);
}

.cert-art {
  display: block;
  width: 100%;
  height: 100%;
}

/* SVG 内文字字体（字号写在 viewBox 坐标上，随整体等比缩放） */
.cert-art-title {
  font-family: "STZhongsong", "华文中宋", "SimSun", "宋体", "Microsoft YaHei",
    serif;
  font-weight: 900;
}

.cert-art-recipient {
  font-family: "SimHei", "黑体", "Microsoft YaHei", sans-serif;
  font-weight: 700;
}

.cert-art-issuer {
  font-family: "SimHei", "黑体", "Microsoft YaHei", sans-serif;
  font-weight: 700;
}

/* 合规角标：明确为版式示意，非扫描件 */
.cert-schema-tag {
  position: absolute;
  right: 6px;
  bottom: 6px;
  font-size: 12px;
  line-height: 1;
  color: #676767;
  background: rgba(255, 255, 255, 0.78);
  padding: 2px 6px;
  border-radius: 3px;
  z-index: 2;
}

/* 平板设备适配 */
@media screen and (min-width: 768px) and (max-width: 992px) {
  .cert-replica {
    font-size: 8.5px;
  }
}

/* 手机设备适配 */
@media screen and (max-width: 768px) {
  .cert-replica {
    font-size: 7px;
  }
}

/* 超小屏幕优化 (<575px)：单列布局，容器接近全屏宽 */
@media screen and (max-width: 575px) {
  .cert-replica {
    font-size: 12px;
  }
}

@media screen and (max-width: 480px) {
  .cert-replica {
    font-size: 10.5px;
  }
}

@media screen and (max-width: 350px) {
  .cert-replica {
    font-size: 9px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/CertReplica.vue